export type ImageProperties = {
  url: string;
  focalPoint: {
    left: number;
    top: number;
  };
  focalPointPercentages: {
    left: number;
    top: number;
  };
  altText?: string;
  width?: number;
  height?: number;
};

const asMediaUrl = (value: unknown): string | undefined => {
  if (typeof value === 'string' && value.length > 0) {
    return value;
  }

  if (value && typeof value === 'object' && 'url' in value) {
    const url = (value as { url: unknown }).url;

    return typeof url === 'string' && url.length > 0 ? url : undefined;
  }

  return undefined;
};

/**
 * Resolves the URL string from an Umbraco media item or its `properties` object.
 *
 * Accepts either:
 * - a media item with a `properties` field (e.g. `IApiMediaWithCropsModel`), or
 * - a bare `properties` object with `src` or `umbracoFile`.
 *
 * The `src`/`umbracoFile` property on Umbraco block/element `properties` comes back
 * in one of two shapes depending on the content model:
 * - a media object such as `{ url: string, ... }`, or
 * - a raw URL string.
 *
 * Returns `undefined` when no usable URL can be resolved.
 */
export function getMediaProperties(input: Record<string, any> | undefined, defaultAltText?: string, fallbackUrl?: string): ImageProperties | undefined {
  if (!input) {
    if (fallbackUrl) {
      return {
        url: fallbackUrl,
        focalPoint: { left: 0.5, top: 0.5 },
        altText: defaultAltText,
        focalPointPercentages: { left: 50, top: 50 },
        height: undefined,
        width: undefined,
      };
    }

    return undefined;
  }

  const source = input.properties?.src ?? input.src ?? input.properties?.umbracoFile ?? input.umbracoFile;
  const url = asMediaUrl(input.url) ?? asMediaUrl(source) ?? fallbackUrl;

  if (!url) {
    return undefined;
  }

  const focalPoint = input.focalPoint ?? source?.focalPoint ?? { left: 0.5, top: 0.5 };
  const altText = input.properties?.altText ?? input.altText ?? defaultAltText;

  return {
    url,
    focalPoint,
    altText,
    width: input.width ? input.width : undefined,
    height: input.height ? input.height : undefined,
    focalPointPercentages: {
      left: Math.floor(focalPoint.left * 100),
      top: Math.floor(focalPoint.top * 100),
    },
  };
}
